import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Percent, TrendingUp, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AmortizationCalculator from '../components/AmortizationCalculator';

export default function Underwriter() {
  const navigate = useNavigate();
  
  const [capRateInputs, setCapRateInputs] = useState({
    noi: 24000,
    purchasePrice: 200000
  });

  const [cashFlowInputs, setCashFlowInputs] = useState({
    purchasePrice: 200000,
    repairCosts: 50000,
    downPayment: 20,
    interestRate: 7.5,
    loanTerm: 25,
    monthlyRent: 2200,
    propertyTax: 3000,
    insurance: 1200,
    targetCashFlow: 300
  });

  const [dscrInputs, setDscrInputs] = useState({
    annualNOI: 24000,
    annualDebtService: 17520,
    propertyValue: 300000,
    loanAmount: 240000,
    arvValue: 400000,
    arvLoanAmount: 300000
  });

  const [arvInputs, setArvInputs] = useState({
    comp1: 185000,
    comp2: 195000,
    comp3: 188000,
    squareFeet: 1850
  });

  const [brrrrInputs, setBrrrrInputs] = useState({
    purchasePrice: 50000,
    repairCosts: 70000,
    constructionLoanAmount: 120000,
    pullBackPercentage: 20,
    refinanceARV: 180000,
    refinanceLTV: 75,
    interestRate: 7.5,
    loanTerm: 30,
    monthlyRent: 1800,
    propertyTax: 2400,
    insurance: 1200,
    targetCashFlow: 300
  });

  const [proFormaInputs, setProFormaInputs] = useState({
    portfolioName: "Southern GA Portfolio",
    purchasePrice: 500000,
    numberOfUnits: 8,
    existingMonthlyIncome: 4800,
    existingMonthlyExpenses: 2400,
    arvMonthlyIncome: 7200,
    arvMonthlyExpenses: 2800,
    manualCapRate: 8.5,
    repairBudget: 150000
  });

  // T12 (Trailing 12 Months) State - Depreciation, Appreciation, CapEx
  const [t12Inputs, setT12Inputs] = useState({
    property_name: 'Investment Property',
    purchase_price: 500000,
    land_value_pct: 20,
    depreciation_years: 27.5,
    annual_noi: 48000,
    appreciation_rate: 3,
    hold_period_years: 5,
    capex_reserve_pct: 5,
    tax_bracket: 32
  });

  // PFS (Personal Financial Statement) State
  const [pfsInputs, setPfsInputs] = useState({
    cash_and_savings: 50000,
    stocks_and_investments: 100000,
    retirement_accounts: 150000,
    real_estate_equity: 200000,
    vehicles: 30000,
    other_assets: 20000,
    mortgages: 300000,
    auto_loans: 15000,
    credit_cards: 5000,
    student_loans: 25000,
    other_liabilities: 10000,
    annual_w2_income: 85000,
    rental_income: 24000,
    other_income: 6000,
    target_dti: 43
  });

  // T12 Calculations
  const t12BuildingValue = t12Inputs.purchase_price * (1 - t12Inputs.land_value_pct / 100);
  const t12AnnualDepreciation = t12BuildingValue / t12Inputs.depreciation_years;
  const t12TaxSavings = t12AnnualDepreciation * (t12Inputs.tax_bracket / 100);
  const t12FutureValue = t12Inputs.purchase_price * Math.pow(1 + t12Inputs.appreciation_rate / 100, t12Inputs.hold_period_years);
  const t12TotalAppreciation = t12FutureValue - t12Inputs.purchase_price;
  const t12AnnualAppreciation = t12TotalAppreciation / t12Inputs.hold_period_years;
  const t12CapExReserve = t12Inputs.annual_noi * (t12Inputs.capex_reserve_pct / 100);
  const t12AdjustedNOI = t12Inputs.annual_noi - t12CapExReserve;
  const t12TotalDepreciation = t12AnnualDepreciation * t12Inputs.hold_period_years;
  const t12EffectiveAnnualReturn = t12Inputs.annual_noi + t12AnnualAppreciation + t12TaxSavings;
  const t12EffectiveROI = t12Inputs.purchase_price > 0 ? ((t12EffectiveAnnualReturn / t12Inputs.purchase_price) * 100).toFixed(2) : 0;

  // PFS Calculations
  const pfsGrossAssets = pfsInputs.cash_and_savings + pfsInputs.stocks_and_investments + pfsInputs.retirement_accounts + pfsInputs.real_estate_equity + pfsInputs.vehicles + pfsInputs.other_assets;
  const pfsTotalLiabilities = pfsInputs.mortgages + pfsInputs.auto_loans + pfsInputs.credit_cards + pfsInputs.student_loans + pfsInputs.other_liabilities;
  const pfsNetWorth = pfsGrossAssets - pfsTotalLiabilities;
  const pfsTotalAnnualIncome = pfsInputs.annual_w2_income + pfsInputs.rental_income + pfsInputs.other_income;
  const pfsMonthlyIncome = pfsTotalAnnualIncome / 12;
  const pfsEstMonthlyDebt = (pfsInputs.mortgages * 0.006) + (pfsInputs.auto_loans * 0.025) + (pfsInputs.credit_cards * 0.03) + (pfsInputs.student_loans * 0.01);
  const pfsCurrentDTI = pfsMonthlyIncome > 0 ? ((pfsEstMonthlyDebt / pfsMonthlyIncome) * 100).toFixed(1) : 0;
  const pfsAvailableDTI = pfsInputs.target_dti - parseFloat(pfsCurrentDTI);
  const pfsMaxNewPayment = Math.max(0, (pfsAvailableDTI / 100) * pfsMonthlyIncome);
  const pfsMaxLoanAmount = pfsMaxNewPayment > 0 ? Math.round(pfsMaxNewPayment / 0.006) : 0;
  const pfsLiquidity = pfsInputs.cash_and_savings + pfsInputs.stocks_and_investments;
  const pfsBankabilityScore = Math.min(100, Math.round(
    (pfsNetWorth > 0 ? 25 : 0) +
    (parseFloat(pfsCurrentDTI) < 30 ? 25 : parseFloat(pfsCurrentDTI) < 40 ? 15 : 5) +
    (pfsLiquidity > 50000 ? 25 : pfsLiquidity > 25000 ? 15 : 5) +
    (pfsTotalAnnualIncome > 100000 ? 25 : pfsTotalAnnualIncome > 60000 ? 15 : 5)
  ));

  const calculateCapRate = () => {
    const capRate = (capRateInputs.noi / capRateInputs.purchasePrice) * 100;
    return capRate.toFixed(2);
  };

  const calculateCashFlow = () => {
    const totalInvestment = cashFlowInputs.purchasePrice + cashFlowInputs.repairCosts;
    const downPaymentAmount = totalInvestment * (cashFlowInputs.downPayment / 100);
    const loanAmount = totalInvestment - downPaymentAmount;
    
    const monthlyInterestRate = cashFlowInputs.interestRate / 100 / 12;
    const numberOfPayments = cashFlowInputs.loanTerm * 12;
    
    const monthlyPI = loanAmount * 
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
    
    const monthlyTax = cashFlowInputs.propertyTax / 12;
    const monthlyInsurance = cashFlowInputs.insurance / 12;
    const monthlyPITI = monthlyPI + monthlyTax + monthlyInsurance;
    
    const monthlyCashFlow = cashFlowInputs.monthlyRent - monthlyPITI;
    
    return {
      downPayment: downPaymentAmount,
      loanAmount: loanAmount,
      monthlyPI: monthlyPI,
      monthlyPITI: monthlyPITI,
      monthlyCashFlow: monthlyCashFlow,
      annualCashFlow: monthlyCashFlow * 12,
      cashOnCashReturn: ((monthlyCashFlow * 12) / downPaymentAmount * 100).toFixed(2)
    };
  };

  const calculateDSCR = () => {
    const dscr = dscrInputs.annualNOI / dscrInputs.annualDebtService;
    const preLeverageDSCR = dscr * 1.12;
    
    // LTV Calculations
    const currentLTV = dscrInputs.propertyValue > 0 
      ? ((dscrInputs.loanAmount / dscrInputs.propertyValue) * 100).toFixed(1) 
      : 0;
    const arvLTV = dscrInputs.arvValue > 0 
      ? ((dscrInputs.arvLoanAmount / dscrInputs.arvValue) * 100).toFixed(1) 
      : 0;
    
    // Equity calculations
    const currentEquity = dscrInputs.propertyValue - dscrInputs.loanAmount;
    const arvEquity = dscrInputs.arvValue - dscrInputs.arvLoanAmount;
    const equityGain = arvEquity - currentEquity;
    
    return {
      afterLeverage: dscr.toFixed(2),
      preLeverage: preLeverageDSCR.toFixed(2),
      meetsRequirement: dscr >= 1.25,
      currentLTV,
      arvLTV,
      currentEquity,
      arvEquity,
      equityGain
    };
  };

  const calculateARV = () => {
    const avgComp = (arvInputs.comp1 + arvInputs.comp2 + arvInputs.comp3) / 3;
    const pricePerSqFt = avgComp / arvInputs.squareFeet;
    return {
      arv: avgComp,
      pricePerSqFt: pricePerSqFt
    };
  };

  const calculateBRRRR = () => {
    const totalInvestment = brrrrInputs.purchasePrice + brrrrInputs.repairCosts;
    const downPayment20Pct = brrrrInputs.constructionLoanAmount * 0.20;
    const bankLoan80Pct = brrrrInputs.constructionLoanAmount * 0.80;
    const developerFee = downPayment20Pct;
    const remainingConstructionFunds = brrrrInputs.constructionLoanAmount - developerFee;
    
    // Pre-ARV DSCR (based on estimated rent before rehab)
    const estimatedPreRehabRent = brrrrInputs.monthlyRent * 0.60;
    const estimatedPreRehabNOI = estimatedPreRehabRent * 12;
    const constructionLoanMonthlyPayment = (bankLoan80Pct * (brrrrInputs.interestRate / 100)) / 12;
    const preRehabAnnualDebtService = constructionLoanMonthlyPayment * 12;
    const preArvDSCR = estimatedPreRehabNOI / preRehabAnnualDebtService;
    
    // Refinance calculations
    const refinanceAmount = brrrrInputs.refinanceARV * (brrrrInputs.refinanceLTV / 100);
    const payoffConstructionLoan = bankLoan80Pct;
    const cashOutAtRefi = refinanceAmount - payoffConstructionLoan;
    const totalCashRecovered = developerFee + cashOutAtRefi;
    const netCashPosition = totalCashRecovered - downPayment20Pct;
    const leftInDeal = totalInvestment - totalCashRecovered;
    
    // Post-ARV calculations
    const monthlyInterestRate = brrrrInputs.interestRate / 100 / 12;
    const numberOfPayments = brrrrInputs.loanTerm * 12;
    const monthlyPI = refinanceAmount * 
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
    
    const monthlyTax = brrrrInputs.propertyTax / 12;
    const monthlyInsurance = brrrrInputs.insurance / 12;
    const monthlyPITI = monthlyPI + monthlyTax + monthlyInsurance;
    const monthlyCashFlow = brrrrInputs.monthlyRent - monthlyPITI;
    const annualNOI = (brrrrInputs.monthlyRent * 12) - ((brrrrInputs.propertyTax + brrrrInputs.insurance) * 1.35);
    const postArvAnnualDebtService = monthlyPI * 12;
    const postArvDSCR = annualNOI / postArvAnnualDebtService;
    
    const leveragedARV = (refinanceAmount / brrrrInputs.refinanceARV) * 100;
    
    return {
      totalInvestment,
      downPayment20Pct,
      bankLoan80Pct,
      developerFee,
      remainingConstructionFunds,
      refinanceAmount,
      payoffConstructionLoan,
      cashOutAtRefi,
      totalCashRecovered,
      netCashPosition,
      leftInDeal,
      monthlyPI,
      monthlyPITI,
      monthlyCashFlow,
      leveragedARV,
      preArvDSCR,
      postArvDSCR,
      meetsTarget: monthlyCashFlow >= brrrrInputs.targetCashFlow && postArvDSCR >= 1.25
    };
  };

  const calculateProForma = () => {
    const existingNOI = (proFormaInputs.existingMonthlyIncome - proFormaInputs.existingMonthlyExpenses) * 12;
    const arvNOI = (proFormaInputs.arvMonthlyIncome - proFormaInputs.arvMonthlyExpenses) * 12;
    const existingValue = existingNOI / (proFormaInputs.manualCapRate / 100);
    const arvValue = arvNOI / (proFormaInputs.manualCapRate / 100);
    const totalProjectCost = proFormaInputs.purchasePrice + proFormaInputs.repairBudget;
    const equityGain = arvValue - totalProjectCost;
    const roi = (equityGain / totalProjectCost) * 100;
    
    return {
      existingNOI,
      arvNOI,
      noiIncrease: arvNOI - existingNOI,
      existingValue,
      arvValue,
      valueIncrease: arvValue - existingValue,
      totalProjectCost,
      equityGain,
      roi
    };
  };

  const capRate = calculateCapRate();
  const cashFlow = calculateCashFlow();
  const dscr = calculateDSCR();
  const arv = calculateARV();
  const brrrr = calculateBRRRR();
  const proForma = calculateProForma();

  const cashFlowData = [
    { name: 'Down Payment', value: cashFlow.downPayment },
    { name: 'Loan Amount', value: cashFlow.loanAmount }
  ];

  const COLORS = ['#22c55e', '#ef4444'];

  const monthlyBreakdownData = [
    { category: 'Rent Income', amount: cashFlowInputs.monthlyRent },
    { category: 'P&I Payment', amount: -cashFlow.monthlyPI },
    { category: 'Property Tax', amount: -(cashFlowInputs.propertyTax / 12) },
    { category: 'Insurance', amount: -(cashFlowInputs.insurance / 12) },
    { category: 'Net Cash Flow', amount: cashFlow.monthlyCashFlow }
  ];

  return (
    <div className="min-h-screen relative z-10 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button 
              data-testid="back-from-underwriter-btn"
              onClick={() => navigate('/')} 
              className="bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-mono font-bold uppercase tracking-tight">Internal Underwriter</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-8">
        <Tabs defaultValue="caprate" className="space-y-6">
          <TabsList className="bg-zinc-950 border border-zinc-800 p-3 grid grid-cols-3 md:grid-cols-9 gap-3 w-full h-auto">
            <TabsTrigger data-testid="caprate-tab" value="caprate" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">Cap Rate</TabsTrigger>
            <TabsTrigger data-testid="cashflow-tab" value="cashflow" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">Cash Flow</TabsTrigger>
            <TabsTrigger data-testid="dscr-tab" value="dscr" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">DSCR</TabsTrigger>
            <TabsTrigger data-testid="arv-tab" value="arv" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">ARV</TabsTrigger>
            <TabsTrigger data-testid="brrrr-tab" value="brrrr" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">BRRRR</TabsTrigger>
            <TabsTrigger data-testid="proforma-tab" value="proforma" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">Pro Forma</TabsTrigger>
            <TabsTrigger data-testid="amort-tab" value="amortization" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">Amort</TabsTrigger>
            <TabsTrigger data-testid="t12-tab" value="t12" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">T12</TabsTrigger>
            <TabsTrigger data-testid="pfs-tab" value="pfs" className="font-mono uppercase text-xs py-4 px-2 data-[state=active]:bg-green-600 data-[state=active]:text-black">PFS</TabsTrigger>
          </TabsList>

          <TabsContent value="caprate">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Inputs</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="noi" className="text-xs font-mono uppercase text-zinc-400">Annual Net Operating Income (NOI)</Label>
                    <Input 
                      data-testid="noi-input"
                      id="noi"
                      type="number" 
                      value={capRateInputs.noi} 
                      onChange={(e) => setCapRateInputs({...capRateInputs, noi: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchasePrice" className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                    <Input 
                      data-testid="purchase-price-input"
                      id="purchasePrice"
                      type="number" 
                      value={capRateInputs.purchasePrice} 
                      onChange={(e) => setCapRateInputs({...capRateInputs, purchasePrice: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-green-500/50 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Results</h2>
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-mono uppercase text-zinc-400 mb-2">Capitalization Rate</p>
                    <p data-testid="caprate-result" className="text-5xl font-mono font-bold text-green-400">{capRate}%</p>
                  </div>
                  <div className="bg-zinc-900 p-4 border border-zinc-800">
                    <p className="text-xs font-mono uppercase text-zinc-500 mb-2">Market Benchmark</p>
                    <p className="text-sm font-mono text-zinc-300">Typical cap rates for distressed properties: <span className="text-green-400 font-bold">8-12%</span></p>
                    <p className="text-sm font-mono text-zinc-300 mt-2">Your cap rate: <span className={`font-bold ${parseFloat(capRate) >= 8 ? 'text-green-400' : 'text-red-400'}`}>{parseFloat(capRate) >= 8 ? 'GOOD' : 'LOW'}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="cashflow">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Inputs</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                      <Input 
                        data-testid="cf-purchase-price"
                        type="number" 
                        value={cashFlowInputs.purchasePrice} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, purchasePrice: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Repair Costs</Label>
                      <Input 
                        data-testid="cf-repair-costs"
                        type="number" 
                        value={cashFlowInputs.repairCosts} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, repairCosts: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Down Payment %</Label>
                      <Input 
                        data-testid="cf-down-payment"
                        type="number" 
                        value={cashFlowInputs.downPayment} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, downPayment: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Interest Rate %</Label>
                      <Input 
                        data-testid="cf-interest-rate"
                        type="number" 
                        step="0.1"
                        value={cashFlowInputs.interestRate} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, interestRate: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Loan Term (Years)</Label>
                    <Input 
                      data-testid="cf-loan-term"
                      type="number" 
                      value={cashFlowInputs.loanTerm} 
                      onChange={(e) => setCashFlowInputs({...cashFlowInputs, loanTerm: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Monthly Rent</Label>
                    <Input 
                      data-testid="cf-monthly-rent"
                      type="number" 
                      value={cashFlowInputs.monthlyRent} 
                      onChange={(e) => setCashFlowInputs({...cashFlowInputs, monthlyRent: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Annual Property Tax</Label>
                      <Input 
                        data-testid="cf-property-tax"
                        type="number" 
                        value={cashFlowInputs.propertyTax} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, propertyTax: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Annual Insurance</Label>
                      <Input 
                        data-testid="cf-insurance"
                        type="number" 
                        value={cashFlowInputs.insurance} 
                        onChange={(e) => setCashFlowInputs({...cashFlowInputs, insurance: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-zinc-950 border border-green-500/50 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Cash Flow Analysis</h2>
                  <div className="space-y-4">
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Down Payment (20%)</span>
                      <span data-testid="cf-result-down" className="font-mono font-bold">${cashFlow.downPayment.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Loan Amount</span>
                      <span data-testid="cf-result-loan" className="font-mono font-bold">${cashFlow.loanAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Monthly P&I</span>
                      <span data-testid="cf-result-pi" className="font-mono font-bold">${cashFlow.monthlyPI.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Monthly PITI</span>
                      <span data-testid="cf-result-piti" className="font-mono font-bold">${cashFlow.monthlyPITI.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 bg-green-500/10 -mx-6 px-6 py-3">
                      <span className="text-sm font-mono uppercase text-green-400">Monthly Cash Flow</span>
                      <span data-testid="cf-result-monthly" className={`text-2xl font-mono font-bold ${cashFlow.monthlyCashFlow >= cashFlowInputs.targetCashFlow ? 'text-green-400' : 'text-red-400'}`}>
                        ${cashFlow.monthlyCashFlow.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-mono text-zinc-400">Cash-on-Cash Return</span>
                      <span data-testid="cf-result-coc" className="text-xl font-mono font-bold text-green-400">{cashFlow.cashOnCashReturn}%</span>
                    </div>
                    <div className="bg-zinc-900 p-3 border border-zinc-800">
                      <p className="text-xs font-mono uppercase text-zinc-500">Target Met</p>
                      <p className={`font-mono font-bold ${cashFlow.monthlyCashFlow >= cashFlowInputs.targetCashFlow ? 'text-green-400' : 'text-red-400'}`}>
                        {cashFlow.monthlyCashFlow >= cashFlowInputs.targetCashFlow ? 'YES - DEAL MEETS TARGET' : 'NO - BELOW TARGET'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Monthly Breakdown</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={monthlyBreakdownData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="category" stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} angle={-15} textAnchor="end" height={80} />
                      <YAxis stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', fontFamily: 'JetBrains Mono' }}
                        formatter={(value) => `$${value.toFixed(2)}`}
                      />
                      <Bar dataKey="amount" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="dscr">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">DSCR Inputs</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Annual NOI</Label>
                    <Input 
                      data-testid="dscr-noi-input"
                      type="number" 
                      value={dscrInputs.annualNOI} 
                      onChange={(e) => setDscrInputs({...dscrInputs, annualNOI: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Annual Debt Service</Label>
                    <Input 
                      data-testid="dscr-debt-input"
                      type="number" 
                      value={dscrInputs.annualDebtService} 
                      onChange={(e) => setDscrInputs({...dscrInputs, annualDebtService: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                    />
                  </div>
                </div>

                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4 mt-8 pt-4 border-t border-zinc-800">LTV / Leverage Analysis</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Current Property Value</Label>
                      <Input 
                        type="number" 
                        value={dscrInputs.propertyValue} 
                        onChange={(e) => setDscrInputs({...dscrInputs, propertyValue: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Current Loan Amount</Label>
                      <Input 
                        type="number" 
                        value={dscrInputs.loanAmount} 
                        onChange={(e) => setDscrInputs({...dscrInputs, loanAmount: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">ARV (After Repair Value)</Label>
                      <Input 
                        type="number" 
                        value={dscrInputs.arvValue} 
                        onChange={(e) => setDscrInputs({...dscrInputs, arvValue: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Refinance Loan Amount</Label>
                      <Input 
                        type="number" 
                        value={dscrInputs.arvLoanAmount} 
                        onChange={(e) => setDscrInputs({...dscrInputs, arvLoanAmount: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2 h-12 text-base"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-zinc-950 border border-green-500/50 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">DSCR Results</h2>
                  <div className="space-y-6">
                    <div>
                      <p className="text-xs font-mono uppercase text-zinc-400 mb-2">DSCR (After Leverage)</p>
                      <p data-testid="dscr-after-result" className={`text-5xl font-mono font-bold ${dscr.meetsRequirement ? 'text-green-400' : 'text-red-400'}`}>{dscr.afterLeverage}</p>
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase text-zinc-400 mb-2">DSCR (Pre-Leverage Est.)</p>
                      <p data-testid="dscr-pre-result" className="text-3xl font-mono font-bold text-green-400">{dscr.preLeverage}</p>
                    </div>
                    <div className={`p-4 border ${dscr.meetsRequirement ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                      <p className="text-xs font-mono uppercase mb-2">{dscr.meetsRequirement ? 'READY FOR BANK' : 'NOT READY'}</p>
                      <p className="text-sm font-mono">{dscr.meetsRequirement ? 'Meets 1.25 DSCR requirement' : 'Adjust deal to meet 1.25 DSCR minimum'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-950 border border-yellow-500/50 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">LTV Analysis</h2>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-xs font-mono uppercase text-zinc-500 border-b border-zinc-800 pb-2">Before (Current)</p>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">Property Value</p>
                        <p className="text-xl font-mono font-bold">${dscrInputs.propertyValue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">Loan Amount</p>
                        <p className="text-xl font-mono font-bold text-yellow-500">${dscrInputs.loanAmount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">LTV Ratio</p>
                        <p className={`text-3xl font-mono font-bold ${parseFloat(dscr.currentLTV) <= 80 ? 'text-green-400' : 'text-yellow-500'}`}>{dscr.currentLTV}%</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">Equity Position</p>
                        <p className="text-xl font-mono font-bold">${dscr.currentEquity.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <p className="text-xs font-mono uppercase text-zinc-500 border-b border-zinc-800 pb-2">After (ARV)</p>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">ARV Value</p>
                        <p className="text-xl font-mono font-bold text-green-400">${dscrInputs.arvValue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">Loan Amount</p>
                        <p className="text-xl font-mono font-bold text-yellow-500">${dscrInputs.arvLoanAmount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">LTV Ratio</p>
                        <p className={`text-3xl font-mono font-bold ${parseFloat(dscr.arvLTV) <= 80 ? 'text-green-400' : 'text-yellow-500'}`}>{dscr.arvLTV}%</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-400">Equity Position</p>
                        <p className="text-xl font-mono font-bold text-green-400">${dscr.arvEquity.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-xs font-mono text-zinc-400">Equity Gain Through Leverage</p>
                    <p className={`text-2xl font-mono font-bold ${dscr.equityGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${dscr.equityGain.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="arv">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Comps</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Comp 1 Value</Label>
                    <Input 
                      data-testid="arv-comp1"
                      type="number" 
                      value={arvInputs.comp1} 
                      onChange={(e) => setArvInputs({...arvInputs, comp1: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Comp 2 Value</Label>
                    <Input 
                      data-testid="arv-comp2"
                      type="number" 
                      value={arvInputs.comp2} 
                      onChange={(e) => setArvInputs({...arvInputs, comp2: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Comp 3 Value</Label>
                    <Input 
                      data-testid="arv-comp3"
                      type="number" 
                      value={arvInputs.comp3} 
                      onChange={(e) => setArvInputs({...arvInputs, comp3: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Subject Property Square Feet</Label>
                    <Input 
                      data-testid="arv-sqft"
                      type="number" 
                      value={arvInputs.squareFeet} 
                      onChange={(e) => setArvInputs({...arvInputs, squareFeet: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-green-500/50 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">After Repair Value</h2>
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-mono uppercase text-zinc-400 mb-2">Estimated ARV</p>
                    <p data-testid="arv-result" className="text-5xl font-mono font-bold text-green-400">${arv.arv.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase text-zinc-400 mb-2">Price Per Square Foot</p>
                    <p data-testid="arv-per-sqft" className="text-3xl font-mono font-bold">${arv.pricePerSqFt.toFixed(2)}</p>
                  </div>
                  <div className="bg-zinc-900 p-4 border border-zinc-800">
                    <p className="text-xs font-mono uppercase text-zinc-500 mb-3">Comp Breakdown</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Comp 1</span>
                        <span className="font-mono">${arvInputs.comp1.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Comp 2</span>
                        <span className="font-mono">${arvInputs.comp2.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Comp 3</span>
                        <span className="font-mono">${arvInputs.comp3.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-zinc-800">
                        <span className="text-sm font-mono text-green-400">Average</span>
                        <span className="font-mono font-bold text-green-400">${arv.arv.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="brrrr">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">BRRRR Strategy Inputs</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                      <Input 
                        data-testid="brrrr-purchase-price"
                        type="number" 
                        value={brrrrInputs.purchasePrice} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, purchasePrice: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Repair Costs</Label>
                      <Input 
                        data-testid="brrrr-repair-costs"
                        type="number" 
                        value={brrrrInputs.repairCosts} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, repairCosts: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Construction Loan</Label>
                      <Input 
                        data-testid="brrrr-construction-loan"
                        type="number" 
                        value={brrrrInputs.constructionLoanAmount} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, constructionLoanAmount: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Refinance ARV</Label>
                      <Input 
                        data-testid="brrrr-refi-arv"
                        type="number" 
                        value={brrrrInputs.refinanceARV} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, refinanceARV: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Refi LTV %</Label>
                      <Input 
                        data-testid="brrrr-refi-ltv"
                        type="number" 
                        value={brrrrInputs.refinanceLTV} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, refinanceLTV: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Interest Rate %</Label>
                      <Input 
                        data-testid="brrrr-interest-rate"
                        type="number" 
                        step="0.1"
                        value={brrrrInputs.interestRate} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, interestRate: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Loan Term (Years)</Label>
                      <Input 
                        data-testid="brrrr-loan-term"
                        type="number" 
                        value={brrrrInputs.loanTerm} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, loanTerm: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Monthly Rent (ARV)</Label>
                    <Input 
                      data-testid="brrrr-monthly-rent"
                      type="number" 
                      value={brrrrInputs.monthlyRent} 
                      onChange={(e) => setBrrrrInputs({...brrrrInputs, monthlyRent: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Annual Property Tax</Label>
                      <Input 
                        data-testid="brrrr-property-tax"
                        type="number" 
                        value={brrrrInputs.propertyTax} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, propertyTax: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Annual Insurance</Label>
                      <Input 
                        data-testid="brrrr-insurance"
                        type="number" 
                        value={brrrrInputs.insurance} 
                        onChange={(e) => setBrrrrInputs({...brrrrInputs, insurance: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-zinc-950 border border-green-500/50 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Construction Financing</h2>
                  <div className="space-y-4">
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Total All-In Cost</span>
                      <span data-testid="brrrr-total-investment" className="font-mono font-bold">${brrrr.totalInvestment.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Construction Loan Amount</span>
                      <span className="font-mono font-bold">${brrrrInputs.constructionLoanAmount.toLocaleString()}</span>
                    </div>
                    <div className="bg-green-500/10 -mx-6 px-6 py-3 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-green-400">20% Down Payment Needed</span>
                        <span data-testid="brrrr-down-20" className="font-mono font-bold text-green-400">${brrrr.downPayment20Pct.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">80% Bank Finances</span>
                        <span data-testid="brrrr-bank-80" className="font-mono font-bold">${brrrr.bankLoan80Pct.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800 bg-green-500/10 -mx-6 px-6 py-2">
                      <span className="text-sm font-mono text-green-400">Developer Fee Pulled (20%)</span>
                      <span data-testid="brrrr-dev-fee" className="font-mono font-bold text-green-400">${brrrr.developerFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Remaining Construction Funds</span>
                      <span data-testid="brrrr-remaining-funds" className="font-mono font-bold">${brrrr.remainingConstructionFunds.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Refinance & Exit</h2>
                  <div className="space-y-4">
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Refinance Amount (75% ARV)</span>
                      <span data-testid="brrrr-refi-amount" className="font-mono font-bold">${brrrr.refinanceAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Payoff Construction Loan (80%)</span>
                      <span data-testid="brrrr-payoff" className="font-mono font-bold text-red-400">-${brrrr.payoffConstructionLoan.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800 bg-green-500/10 -mx-6 px-6 py-2">
                      <span className="text-sm font-mono text-green-400">Cash Out at Refi</span>
                      <span data-testid="brrrr-cash-out" className="font-mono font-bold text-green-400">${brrrr.cashOutAtRefi.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono uppercase text-zinc-300">Total Cash Recovered</span>
                      <span data-testid="brrrr-total-recovered" className="text-xl font-mono font-bold text-green-400">${brrrr.totalCashRecovered.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800 bg-green-500/10 -mx-6 px-6 py-2">
                      <span className="text-sm font-mono uppercase text-green-400">Net Cash Position</span>
                      <span data-testid="brrrr-net-cash" className="text-2xl font-mono font-bold text-green-400">${brrrr.netCashPosition.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pb-2 border-b border-zinc-800">
                      <span className="text-sm font-mono text-zinc-400">Capital Left in Deal</span>
                      <span data-testid="brrrr-left-in-deal" className="font-mono font-bold">${brrrr.leftInDeal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-mono text-zinc-400">Leveraged ARV</span>
                      <span data-testid="brrrr-leveraged-arv" className="font-mono font-bold">{brrrr.leveragedARV.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Cash Flow & DSCR Analysis</h2>
                  <div className="space-y-4">
                    <div className="bg-zinc-900 p-3 border border-zinc-800">
                      <p className="text-xs font-mono uppercase text-zinc-500 mb-2">Pre-ARV (Construction Period)</p>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">DSCR</span>
                        <span data-testid="brrrr-pre-dscr" className={`font-mono font-bold ${brrrr.preArvDSCR >= 1.0 ? 'text-green-400' : 'text-red-400'}`}>{brrrr.preArvDSCR.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="bg-zinc-900 p-3 border border-green-500/30">
                      <p className="text-xs font-mono uppercase text-green-400 mb-2">Post-ARV (Stabilized)</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm font-mono text-zinc-400">Monthly PITI</span>
                          <span data-testid="brrrr-monthly-piti" className="font-mono">${brrrr.monthlyPITI.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-mono text-zinc-400">Monthly Cash Flow</span>
                          <span data-testid="brrrr-monthly-cf" className={`font-mono font-bold ${brrrr.monthlyCashFlow >= brrrrInputs.targetCashFlow ? 'text-green-400' : 'text-red-400'}`}>
                            ${brrrr.monthlyCashFlow.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-zinc-800">
                          <span className="text-sm font-mono text-zinc-400">DSCR</span>
                          <span data-testid="brrrr-post-dscr" className={`text-xl font-mono font-bold ${brrrr.postArvDSCR >= 1.25 ? 'text-green-400' : 'text-red-400'}`}>{brrrr.postArvDSCR.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 border ${brrrr.meetsTarget ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <p className="text-xs font-mono uppercase mb-2">{brrrr.meetsTarget ? 'DEAL WORKS ✓' : 'NEEDS ADJUSTMENT'}</p>
                  <p className="text-sm font-mono">
                    {brrrr.meetsTarget 
                      ? `Net cash: $${brrrr.netCashPosition.toLocaleString()} | DSCR: ${brrrr.postArvDSCR.toFixed(2)} | CF: $${brrrr.monthlyCashFlow.toFixed(0)}/mo` 
                      : `Adjust inputs to achieve 1.25+ DSCR and $${brrrrInputs.targetCashFlow}/mo cash flow`}
                  </p>
                </div>
              </div>
          </TabsContent>

          <TabsContent value="proforma">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Portfolio Inputs</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Portfolio Name</Label>
                    <Input 
                      data-testid="proforma-name"
                      type="text" 
                      value={proFormaInputs.portfolioName} 
                      onChange={(e) => setProFormaInputs({...proFormaInputs, portfolioName: e.target.value})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                      <Input 
                        data-testid="proforma-purchase"
                        type="number" 
                        value={proFormaInputs.purchasePrice} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, purchasePrice: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Repair Budget</Label>
                      <Input 
                        data-testid="proforma-repairs"
                        type="number" 
                        value={proFormaInputs.repairBudget} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, repairBudget: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Number of Units</Label>
                    <Input 
                      data-testid="proforma-units"
                      type="number" 
                      value={proFormaInputs.numberOfUnits} 
                      onChange={(e) => setProFormaInputs({...proFormaInputs, numberOfUnits: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Existing Monthly Income</Label>
                      <Input 
                        data-testid="proforma-existing-income"
                        type="number" 
                        value={proFormaInputs.existingMonthlyIncome} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, existingMonthlyIncome: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Existing Monthly Expenses</Label>
                      <Input 
                        data-testid="proforma-existing-expenses"
                        type="number" 
                        value={proFormaInputs.existingMonthlyExpenses} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, existingMonthlyExpenses: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">ARV Monthly Income</Label>
                      <Input 
                        data-testid="proforma-arv-income"
                        type="number" 
                        value={proFormaInputs.arvMonthlyIncome} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, arvMonthlyIncome: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">ARV Monthly Expenses</Label>
                      <Input 
                        data-testid="proforma-arv-expenses"
                        type="number" 
                        value={proFormaInputs.arvMonthlyExpenses} 
                        onChange={(e) => setProFormaInputs({...proFormaInputs, arvMonthlyExpenses: parseFloat(e.target.value) || 0})} 
                        className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Market Cap Rate %</Label>
                    <Input 
                      data-testid="proforma-cap-rate"
                      type="number" 
                      step="0.1"
                      value={proFormaInputs.manualCapRate} 
                      onChange={(e) => setProFormaInputs({...proFormaInputs, manualCapRate: parseFloat(e.target.value) || 0})} 
                      className="bg-zinc-900 border-zinc-800 font-mono text-white rounded-none mt-2"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-zinc-950 border border-green-500/50 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Pro Forma Analysis</h2>
                <div className="space-y-6">
                  <div className="bg-zinc-900 p-4 border border-zinc-800">
                    <p className="text-xs font-mono uppercase text-zinc-500 mb-3">Current State</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Existing NOI (Annual)</span>
                        <span data-testid="proforma-existing-noi" className="font-mono">${proForma.existingNOI.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Existing Value @ {proFormaInputs.manualCapRate}%</span>
                        <span data-testid="proforma-existing-value" className="font-mono">${proForma.existingValue.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-zinc-900 p-4 border border-green-500/30">
                    <p className="text-xs font-mono uppercase text-green-400 mb-3">After Repair Value (ARV)</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">ARV NOI (Annual)</span>
                        <span data-testid="proforma-arv-noi" className="font-mono text-green-400">${proForma.arvNOI.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">NOI Increase</span>
                        <span data-testid="proforma-noi-increase" className="font-mono text-green-400">+${proForma.noiIncrease.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-zinc-800">
                        <span className="text-sm font-mono text-zinc-400">ARV @ {proFormaInputs.manualCapRate}%</span>
                        <span data-testid="proforma-arv-value" className="font-mono font-bold text-green-400">${proForma.arvValue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Value Increase</span>
                        <span data-testid="proforma-value-increase" className="font-mono text-green-400">+${proForma.valueIncrease.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-zinc-900 p-4 border border-zinc-800">
                    <p className="text-xs font-mono uppercase text-zinc-500 mb-3">Investment Summary</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-mono text-zinc-400">Total Project Cost</span>
                        <span data-testid="proforma-total-cost" className="font-mono">${proForma.totalProjectCost.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-zinc-800">
                        <span className="text-sm font-mono uppercase text-green-400">Equity Gain</span>
                        <span data-testid="proforma-equity-gain" className="text-xl font-mono font-bold text-green-400">${proForma.equityGain.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-mono uppercase text-green-400">ROI</span>
                        <span data-testid="proforma-roi" className="text-2xl font-mono font-bold text-green-400">{proForma.roi.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-500/10 p-4 border border-green-500/30">
                    <p className="text-xs font-mono uppercase text-green-400 mb-2">Bank Presentation Ready</p>
                    <p className="text-sm font-mono text-zinc-300">
                      {proFormaInputs.portfolioName} delivers {proForma.roi.toFixed(0)}% ROI with ${(proForma.arvNOI / 12).toFixed(0)}/mo per unit cash flow across {proFormaInputs.numberOfUnits} units
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="amortization">
            <AmortizationCalculator />
          </TabsContent>

          {/* T12 - Depreciation, Appreciation, CapEx */}
          <TabsContent value="t12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 p-6">
                <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">T12 Analysis Inputs</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-zinc-400">Property Name</Label>
                    <Input
                      value={t12Inputs.property_name}
                      onChange={(e) => setT12Inputs({...t12Inputs, property_name: e.target.value})}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                        <Input
                          type="number"
                          value={t12Inputs.purchase_price}
                          onChange={(e) => setT12Inputs({...t12Inputs, purchase_price: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Land Value %</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={t12Inputs.land_value_pct}
                          onChange={(e) => setT12Inputs({...t12Inputs, land_value_pct: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Depreciation Years</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={t12Inputs.depreciation_years}
                        onChange={(e) => setT12Inputs({...t12Inputs, depreciation_years: parseFloat(e.target.value) || 27.5})}
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base"
                      />
                      <p className="text-xs text-zinc-600 mt-1">27.5 residential / 39 commercial</p>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Tax Bracket %</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={t12Inputs.tax_bracket}
                          onChange={(e) => setT12Inputs({...t12Inputs, tax_bracket: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Annual NOI</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                        <Input
                          type="number"
                          value={t12Inputs.annual_noi}
                          onChange={(e) => setT12Inputs({...t12Inputs, annual_noi: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">CapEx Reserve %</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={t12Inputs.capex_reserve_pct}
                          onChange={(e) => setT12Inputs({...t12Inputs, capex_reserve_pct: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Appreciation Rate %</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="0.5"
                          value={t12Inputs.appreciation_rate}
                          onChange={(e) => setT12Inputs({...t12Inputs, appreciation_rate: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Hold Period (Years)</Label>
                      <Input
                        type="number"
                        value={t12Inputs.hold_period_years}
                        onChange={(e) => setT12Inputs({...t12Inputs, hold_period_years: parseInt(e.target.value) || 5})}
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Depreciation Analysis */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-green-500 mb-4">Depreciation</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Building Value</p>
                      <p className="text-xl font-mono font-bold">${t12BuildingValue.toLocaleString()}</p>
                      <p className="text-xs text-zinc-600">{100 - t12Inputs.land_value_pct}% of purchase price</p>
                    </div>
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Annual Depreciation</p>
                      <p className="text-xl font-mono font-bold text-green-400">${t12AnnualDepreciation.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-800">
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Tax Savings (Annual)</p>
                      <p className="text-xl font-mono font-bold text-green-400">${t12TaxSavings.toLocaleString()}</p>
                      <p className="text-xs text-zinc-600">At {t12Inputs.tax_bracket}% bracket</p>
                    </div>
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Total Depreciation ({t12Inputs.hold_period_years} yrs)</p>
                      <p className="text-xl font-mono font-bold">${t12TotalDepreciation.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Appreciation Analysis */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-green-500 mb-4">Compound Appreciation</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Future Value (Year {t12Inputs.hold_period_years})</p>
                      <p className="text-xl font-mono font-bold text-green-400">${t12FutureValue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Total Appreciation</p>
                      <p className="text-xl font-mono font-bold text-green-400">${t12TotalAppreciation.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-xs font-mono text-zinc-500">Annual Appreciation (Avg)</p>
                    <p className="text-xl font-mono font-bold">${t12AnnualAppreciation.toLocaleString()}</p>
                  </div>
                </div>

                {/* CapEx & Adjusted NOI */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-green-500 mb-4">CapEx & Adjusted Returns</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-mono text-zinc-500">CapEx Reserve (Annual)</p>
                      <p className="text-xl font-mono font-bold text-red-400">-${t12CapExReserve.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-mono text-zinc-500">Adjusted NOI</p>
                      <p className="text-xl font-mono font-bold">${t12AdjustedNOI.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-800 bg-green-500/10 -mx-6 -mb-6 px-6 py-4">
                    <p className="text-xs font-mono text-green-400 uppercase mb-2">Effective Annual Return</p>
                    <p className="text-3xl font-mono font-bold text-green-400">${t12EffectiveAnnualReturn.toLocaleString()}</p>
                    <p className="text-sm text-zinc-500 mt-1">NOI + Appreciation + Tax Savings = {t12EffectiveROI}% ROI</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* PFS - Personal Financial Statement */}
          <TabsContent value="pfs">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* Assets */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-green-500 mb-6">Assets</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Cash & Savings</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.cash_and_savings}
                            onChange={(e) => setPfsInputs({...pfsInputs, cash_and_savings: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Stocks & Investments</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.stocks_and_investments}
                            onChange={(e) => setPfsInputs({...pfsInputs, stocks_and_investments: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Retirement Accounts</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.retirement_accounts}
                            onChange={(e) => setPfsInputs({...pfsInputs, retirement_accounts: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Real Estate Equity</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.real_estate_equity}
                            onChange={(e) => setPfsInputs({...pfsInputs, real_estate_equity: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Vehicles</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.vehicles}
                            onChange={(e) => setPfsInputs({...pfsInputs, vehicles: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Other Assets</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.other_assets}
                            onChange={(e) => setPfsInputs({...pfsInputs, other_assets: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-zinc-800">
                      <p className="text-xs font-mono text-zinc-500">TOTAL ASSETS</p>
                      <p className="text-2xl font-mono font-bold text-green-400">${pfsGrossAssets.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Liabilities */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-red-500 mb-6">Liabilities</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Mortgages</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.mortgages}
                            onChange={(e) => setPfsInputs({...pfsInputs, mortgages: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Auto Loans</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.auto_loans}
                            onChange={(e) => setPfsInputs({...pfsInputs, auto_loans: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Credit Cards</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.credit_cards}
                            onChange={(e) => setPfsInputs({...pfsInputs, credit_cards: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Student Loans</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.student_loans}
                            onChange={(e) => setPfsInputs({...pfsInputs, student_loans: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-zinc-400">Other Liabilities</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                        <Input
                          type="number"
                          value={pfsInputs.other_liabilities}
                          onChange={(e) => setPfsInputs({...pfsInputs, other_liabilities: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-zinc-800">
                      <p className="text-xs font-mono text-zinc-500">TOTAL LIABILITIES</p>
                      <p className="text-2xl font-mono font-bold text-red-400">${pfsTotalLiabilities.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Income */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-blue-500 mb-6">Income</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Annual W2 Income</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.annual_w2_income}
                            onChange={(e) => setPfsInputs({...pfsInputs, annual_w2_income: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Annual Rental Income</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.rental_income}
                            onChange={(e) => setPfsInputs({...pfsInputs, rental_income: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Other Income</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                          <Input
                            type="number"
                            value={pfsInputs.other_income}
                            onChange={(e) => setPfsInputs({...pfsInputs, other_income: parseFloat(e.target.value) || 0})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase text-zinc-400">Target DTI %</Label>
                        <div className="relative mt-1">
                          <Input
                            type="number"
                            value={pfsInputs.target_dti}
                            onChange={(e) => setPfsInputs({...pfsInputs, target_dti: parseFloat(e.target.value) || 43})}
                            className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-zinc-800">
                      <p className="text-xs font-mono text-zinc-500">TOTAL ANNUAL INCOME</p>
                      <p className="text-2xl font-mono font-bold text-blue-400">${pfsTotalAnnualIncome.toLocaleString()}</p>
                      <p className="text-sm text-zinc-500">Monthly: ${pfsMonthlyIncome.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Net Worth */}
                <div className="bg-zinc-950 border border-green-500 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-green-500 mb-4">Net Worth</h2>
                  <p className={`text-4xl font-mono font-bold ${pfsNetWorth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${pfsNetWorth.toLocaleString()}
                  </p>
                  <p className="text-sm text-zinc-500 mt-2">Assets - Liabilities</p>
                </div>

                {/* Bankability Analysis */}
                <div className="bg-zinc-950 border border-zinc-800 p-6">
                  <h2 className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-4">Bankability Analysis</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Current DTI</p>
                        <p className={`text-xl font-mono font-bold ${parseFloat(pfsCurrentDTI) < 30 ? 'text-green-400' : parseFloat(pfsCurrentDTI) < 43 ? 'text-yellow-500' : 'text-red-400'}`}>
                          {pfsCurrentDTI}%
                        </p>
                        <p className="text-xs text-zinc-600">Target: {pfsInputs.target_dti}%</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Available DTI</p>
                        <p className={`text-xl font-mono font-bold ${pfsAvailableDTI > 10 ? 'text-green-400' : pfsAvailableDTI > 0 ? 'text-yellow-500' : 'text-red-400'}`}>
                          {pfsAvailableDTI.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-800">
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Max New Monthly Payment</p>
                        <p className="text-xl font-mono font-bold text-green-400">${pfsMaxNewPayment.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Est. Max Loan Amount</p>
                        <p className="text-xl font-mono font-bold text-green-400">${pfsMaxLoanAmount.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-800">
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Liquidity</p>
                        <p className="text-xl font-mono font-bold">${pfsLiquidity.toLocaleString()}</p>
                        <p className="text-xs text-zinc-600">Cash + Investments</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono text-zinc-500">Bankability Score</p>
                        <p className={`text-3xl font-mono font-bold ${pfsBankabilityScore >= 75 ? 'text-green-400' : pfsBankabilityScore >= 50 ? 'text-yellow-500' : 'text-red-400'}`}>
                          {pfsBankabilityScore}/100
                        </p>
                        <p className="text-xs text-zinc-600">
                          {pfsBankabilityScore >= 75 ? 'Excellent' : pfsBankabilityScore >= 50 ? 'Good' : 'Needs Work'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}